import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, addDoc, query, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { FaPlus, FaTrash, FaSave, FaClipboardList, FaFilter, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import styles from './ManualDataEntryModal.module.css';

const createBlankRow = () => ({
  _isNew: true,
  _visitId: null,
  projectId: '',
  merkez: '',
  visitNumber: '',
  visitType: 'hastane',
  siteCoordinator: '',
  patientId: '',
  sourceDocument: '', // Kaynak Döküman
  sourceDocumentCompletionTime: '', // Kaynak Döküman Tamamlanma Saati
  screenFail: '',
  dropPatient: '',
  visitDate: '',
  visitStartTime: '',
  protocolDeviation: '',
  capaOpened: '',
  comments: '',
  ecrfCompletionDate: '',
  ecrfCompletionTime: '', // Yeni eCRF tamamlanma saati alanı
  sourceDocCompletionDate: '',
  sourceDocCompletionTime: '', // Yeni Kaynak Dok. tamamlanma saati alanı
  signatureCompletionDate: '',
  processEndDate: '',
  slaTarget: '',
  issueOccurred: '',
});

const visitToRow = (v) => {
  const ev = v.visitEvaluation || {};
  return {
    _isNew: false,
    _visitId: v.id,
    projectId: v.projectId || '',
    merkez: v.hospital || v.merkez || v.city || '',
    visitNumber: v.visitNumber || '',
    visitType: v.visitType || 'hastane',
    siteCoordinator: v.selectedCra || v.doctor || '',
    patientId: v.patientId || '',
    sourceDocument: v.sourceDocument || '', // Kaynak Döküman
    sourceDocumentCompletionTime: v.sourceDocumentCompletionTime || '', // Kaynak Döküman Tamamlanma Saati
    screenFail: ev.screenFail || '',
    dropPatient: ev.dropPatient || '',
    visitDate: v.firstVisitDate || v.date || '',
    visitStartTime: v.startTime || '',
    protocolDeviation: ev.protocolDeviation || '',
    capaOpened: ev.capaOpened || '',
    comments: ev.comments || '',
    ecrfCompletionDate: ev.ecrfCompletionDate || '',
    ecrfCompletionTime: ev.ecrfCompletionTime || '', // Yeni eCRF tamamlanma saati alanı
    sourceDocCompletionDate: ev.sourceDocCompletionDate || '',
    sourceDocCompletionTime: ev.sourceDocCompletionTime || '', // Yeni Kaynak Dok. tamamlanma saati alanı
    signatureCompletionDate: ev.signatureCompletionDate || '',
    processEndDate: ev.processEndDate || '',
    slaTarget: ev.slaTarget || '',
    issueOccurred: ev.issueOccurred || '',
  };
};

const rowToFirestore = (row, employeeId, employeeName, projectId) => ({
  patientId: row.patientId,
  visitNumber: row.visitNumber,
  visitType: row.visitType || 'hastane',
  date: row.visitDate,
  firstVisitDate: row.visitDate,
  startTime: row.visitStartTime,
  hospital: row.merkez,
  merkez: row.merkez,
  selectedCra: row.siteCoordinator,
  sourceDocument: row.sourceDocument, // Kaynak Döküman
  sourceDocumentCompletionTime: row.sourceDocumentCompletionTime, // Kaynak Döküman Tamamlanma Saati
  projectId: projectId || row.projectId || '',
  employeeId,
  employeeName,
  lastUpdated: new Date().toISOString(),
  visitEvaluation: {
    screenFail: row.screenFail,
    dropPatient: row.dropPatient,
    protocolDeviation: row.protocolDeviation,
    capaOpened: row.capaOpened,
    comments: row.comments,
    ecrfCompletionDate: row.ecrfCompletionDate,
    ecrfCompletionTime: row.ecrfCompletionTime, // Yeni eCRF tamamlanma saati alanı
    sourceDocCompletionDate: row.sourceDocCompletionDate,
    sourceDocCompletionTime: row.sourceDocCompletionTime, // Yeni Kaynak Dok. tamamlanma saati alanı
    signatureCompletionDate: row.signatureCompletionDate,
    processEndDate: row.processEndDate,
    slaTarget: row.slaTarget,
    issueOccurred: row.issueOccurred,
  },
});

const calcCycleTime = (visitDate, processEndDate) => {
  if (!visitDate || !processEndDate) return '';
  const d1 = new Date(visitDate);
  const d2 = new Date(processEndDate);
  if (isNaN(d1) || isNaN(d2)) return '';
  return String(Math.floor((d2 - d1) / (1000 * 60 * 60 * 24)));
};

const EMPTY_FILTERS = {
  merkez: '',
  visitNumber: '',
  visitType: '',
  siteCoordinator: '',
  patientId: '',
  sourceDocument: '', // Kaynak Döküman filtresi
  sourceDocumentCompletionTime: '', // Kaynak Döküman Saati filtresi
  screenFail: '',
  dropPatient: '',
  visitDateFrom: '', visitDateTo: '',
  protocolDeviation: '',
  capaOpened: '',
  comments: '',
  ecrfCompletionDateFrom: '', ecrfCompletionDateTo: '',
  sourceDocCompletionDateFrom: '', sourceDocCompletionDateTo: '',
  signatureCompletionDateFrom: '', signatureCompletionDateTo: '',
  processEndDateFrom: '', processEndDateTo: '',
  slaTarget: '',
  issueOccurred: '',
};

const HastaneDataEntryContent = ({ workers, selectedSMO, selectedWorkerId, selectedProjectId, onDataSaved }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const loadIdRef = useRef(0);
  const [hospitals, setHospitals] = useState([]);
  const [cras, setCras] = useState([]);

  // Fetch Centers and CRAs on mount
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const centersSnap = await getDocs(collection(db, 'Centers'));
        const hospitalsList = centersSnap.docs
          .map(d => ({ id: d.id, name: d.data()['Adı'] || d.id }))
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        setHospitals(hospitalsList);

        const extSnap = await getDocs(collection(db, 'ExternalPersonnels'));
        const crasList = extSnap.docs
          .filter(d => {
            const data = d.data();
            const unvan = (data['Ünvan'] || '').toUpperCase();
            const personnelType = (data['Personel Tipi'] || '').toUpperCase();
            const status = (data['Durum'] || '').toLowerCase();
            return status === 'aktif' && (unvan.includes('CRA') || personnelType.includes('CRA'));
          })
          .map(d => {
            const data = d.data();
            return {
              id: d.id,
              name: `${data['Adı'] || ''} ${data['Soyadı'] || ''}`.trim(),
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        setCras(crasList);
      } catch (err) {
        console.error('Error fetching centers/CRAs:', err);
      }
    };
    fetchLookups();
  }, []);

  const hospitalMap = useMemo(() => {
    const map = {};
    hospitals.forEach(h => { map[h.id] = h.name; });
    return map;
  }, [hospitals]);

  const craMap = useMemo(() => {
    const map = {};
    cras.forEach(c => { map[c.id] = c.name; });
    return map;
  }, [cras]);

  const getHospitalName = useCallback((id) => hospitalMap[id] || id || '—', [hospitalMap]);
  const getCraName = useCallback((id) => craMap[id] || id || '—', [craMap]);

  const selectedWorker = workers.find(w => w.id === selectedWorkerId);
  const workerProjects = selectedWorker?.projects || [];

  const getProjectName = useCallback((projectId) => {
    const proj = workerProjects.find(p => p.projectId === projectId);
    return proj ? proj.projectTitle : projectId || '—';
  }, [workerProjects]);

  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const hasActiveFilters = Object.values(filters).some(v => v !== '');
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const toggleFilters = useCallback(() => setShowFilters(prev => !prev), []);

  const loadVisits = useCallback(async () => {
    if (!selectedWorkerId) { setRows([]); return; }
    const myLoad = ++loadIdRef.current;
    setLoading(true);
    try {
      const visitsRef = collection(db, 'Visits');
      const q = selectedProjectId
        ? query(visitsRef, where('employeeId', '==', selectedWorkerId), where('projectId', '==', selectedProjectId), where('visitType', '==', 'hastane'))
        : query(visitsRef, where('employeeId', '==', selectedWorkerId), where('visitType', '==', 'hastane'));
      const snap = await getDocs(q);
      if (myLoad !== loadIdRef.current) return;
      const visitRows = snap.docs.map(d => visitToRow({ id: d.id, ...d.data() }));
      visitRows.sort((a, b) => (a.visitDate || '').localeCompare(b.visitDate || ''));
      setRows(visitRows);
    } catch (err) {
      if (myLoad !== loadIdRef.current) return;
      console.error('Error loading hastane visits:', err);
      toast.error('Vizitler yüklenirken hata oluştu');
    } finally {
      if (myLoad !== loadIdRef.current) return;
      setLoading(false);
    }
  }, [selectedWorkerId, selectedProjectId]);

  useEffect(() => {
    if (selectedWorkerId) {
      loadVisits();
    } else {
      setRows([]);
    }
  }, [selectedWorkerId, loadVisits]);

  useEffect(() => {
    setFilters(EMPTY_FILTERS);
  }, [selectedWorkerId, selectedProjectId]);

  const filteredRows = useMemo(() => {
    const dateInRange = (val, from, to) => {
      if (!from && !to) return true;
      if (!val) return false;
      if (from && val < from) return false;
      if (to && val > to) return false;
      return true;
    };
    return rows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .filter(({ row }) => {
        if (filters.merkez) {
          const merkezName = (hospitalMap[row.merkez] || row.merkez || '').toLowerCase();
          if (!merkezName.includes(filters.merkez.toLowerCase())) return false;
        }
        if (filters.visitNumber && !String(row.visitNumber).toLowerCase().includes(filters.visitNumber.toLowerCase())) return false;
        if (filters.visitType && row.visitType !== filters.visitType) return false;
        if (filters.siteCoordinator) {
          const craName = (craMap[row.siteCoordinator] || row.siteCoordinator || '').toLowerCase();
          if (!craName.includes(filters.siteCoordinator.toLowerCase())) return false;
        }
        if (filters.patientId && !row.patientId.toLowerCase().includes(filters.patientId.toLowerCase())) return false;
        if (filters.screenFail && row.screenFail !== filters.screenFail) return false;
        if (filters.dropPatient && row.dropPatient !== filters.dropPatient) return false;
        if (filters.protocolDeviation && row.protocolDeviation !== filters.protocolDeviation) return false;
        if (filters.capaOpened && row.capaOpened !== filters.capaOpened) return false;
        if (filters.comments && !row.comments.toLowerCase().includes(filters.comments.toLowerCase())) return false;
        if (filters.slaTarget && !String(row.slaTarget).includes(filters.slaTarget)) return false;
        if (filters.issueOccurred && row.issueOccurred !== filters.issueOccurred) return false;
        if (!dateInRange(row.visitDate, filters.visitDateFrom, filters.visitDateTo)) return false;
        if (!dateInRange(row.ecrfCompletionDate, filters.ecrfCompletionDateFrom, filters.ecrfCompletionDateTo)) return false;
        if (!dateInRange(row.sourceDocCompletionDate, filters.sourceDocCompletionDateFrom, filters.sourceDocCompletionDateTo)) return false;
        if (!dateInRange(row.signatureCompletionDate, filters.signatureCompletionDateFrom, filters.signatureCompletionDateTo)) return false;
        if (!dateInRange(row.processEndDate, filters.processEndDateFrom, filters.processEndDateTo)) return false;
        return true;
      });
  }, [rows, filters, hospitalMap, craMap]);

  const handleCellChange = (index, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addRow = () => setRows(prev => [...prev, createBlankRow()]);
  const removeRow = (index) => setRows(prev => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (rows.length === 0) { toast.warn('Kaydedilecek veri bulunamadı'); return; }
    if (!selectedWorkerId) { toast.warn('Önce bir çalışan seçiniz'); return; }

    const worker = workers.find(w => w.id === selectedWorkerId);
    const employeeName = worker ? `${worker.name} ${worker.surname}` : '';

    setSaving(true);
    let savedCount = 0, errorCount = 0;

    try {
      for (const row of rows) {
        const data = rowToFirestore(row, selectedWorkerId, employeeName, selectedProjectId);
        try {
          if (row._visitId && !row._isNew) {
            const visitRef = doc(db, 'Visits', row._visitId);
            const currentDoc = await getDoc(visitRef);
            if (currentDoc.exists()) {
              const merged = {
                ...currentDoc.data(),
                ...data,
                visitEvaluation: { ...currentDoc.data().visitEvaluation, ...data.visitEvaluation },
              };
              await updateDoc(visitRef, merged);
            } else {
              await updateDoc(visitRef, data);
            }
            savedCount++;
          } else {
            await addDoc(collection(db, 'Visits'), { ...data, createdAt: new Date().toISOString(), manualEntry: true });
            savedCount++;
          }
        } catch (err) {
          console.error('Error saving row:', err);
          errorCount++;
        }
      }
      if (savedCount > 0) {
        toast.success(`${savedCount} kayıt başarıyla kaydedildi`);
        if (onDataSaved) onDataSaved();
        await loadVisits();
      }
      if (errorCount > 0) toast.error(`${errorCount} kayıt kaydedilemedi`);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Kaydetme sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const existingCount = rows.filter(r => !r._isNew).length;
  const newCount = rows.filter(r => r._isNew).length;

  return (
    <>
      {/* Stats / Actions bar */}
      <div className={styles.selectorBar}>
        {rows.length > 0 && (
          <span className={styles.visitCountBadge}>
            <FaClipboardList />
            {hasActiveFilters ? `${filteredRows.length} / ${rows.length} kayıt` : `${rows.length} kayıt`}
            {existingCount > 0 && <span> ({existingCount} mevcut)</span>}
            {newCount > 0 && <span> ({newCount} yeni)</span>}
          </span>
        )}
        {hasActiveFilters && (
          <button className={styles.clearFiltersBtn} onClick={clearFilters} title="Tüm filtreleri temizle">
            <FaFilter /> Filtreleri Temizle
          </button>
        )}
        {selectedWorkerId && (
          <button className={styles.addRowButton} onClick={addRow}>
            <FaPlus /> Yeni Satır Ekle
          </button>
        )}
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        {loading ? (
          <div className={styles.emptyState}>
            <div className={styles.spinner} style={{ width: 32, height: 32, borderWidth: 3 }} />
            <span className={styles.emptyStateText}>Vizitler yükleniyor...</span>
          </div>
        ) : !selectedWorkerId ? (
          <div className={styles.emptyState}>
            <FaClipboardList className={styles.emptyStateIcon} />
            <span className={styles.emptyStateText}>Veri girişi yapmak için bir çalışan seçiniz</span>
          </div>
        ) : rows.length === 0 ? (
          <div className={styles.emptyState}>
            <FaClipboardList className={styles.emptyStateIcon} />
            <span className={styles.emptyStateText}>
              Bu çalışana ait hastane viziti bulunamadı. Yeni satır ekleyerek veri girebilirsiniz.
            </span>
            <button className={styles.addRowButton} onClick={addRow} style={{ marginTop: '0.5rem' }}>
              <FaPlus /> Yeni Satır Ekle
            </button>
          </div>
        ) : (
          <table className={styles.dataTable}>
            <thead>
              <tr className={styles.groupHeaderRow}>
                <th className={styles.grpActions} rowSpan={showFilters ? 3 : 2} style={{ minWidth: 32 }}>#</th>
                <th className={styles.grpIdentification} colSpan={6}>Kimlik</th>
                <th className={styles.grpVisit} colSpan={2}>Hasta Durumu</th>
                <th className={styles.grpSchedule} colSpan={2}>Vizit</th>
                <th className={styles.grpTravelBefore} colSpan={3}>Protokol & CAPA</th>
                <th className={styles.grpSource} colSpan={4}>Tamamlanma Tarihleri</th>
                <th className={styles.grpOutcome} colSpan={4}>Süreç & SLA</th>
                <th className={styles.grpIssue} colSpan={1}>Sorun</th>
                <th className={styles.grpActions} rowSpan={showFilters ? 3 : 2} style={{ minWidth: 36 }}>Sil</th>
              </tr>
              <tr className={styles.columnHeaderRow}>
                {/* Kimlik */}
                <th>Proje Adı</th>
                <th><span className={styles.colHeaderContent}>Merkez <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th><span className={styles.colHeaderContent}>Visit ID <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th>Visit Tipi</th>
                <th><span className={styles.colHeaderContent}>CRA <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th><span className={styles.colHeaderContent}>Hasta ID <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                {/* Kaynak Döküman */}
                <th><span className={styles.colHeaderContent}>Kaynak Döküman <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th><span className={styles.colHeaderContent}>Kaynak Döküman Saati <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                {/* Hasta Durumu */}
                <th>Screen Fail?</th>
                <th>Drop Patient?</th>
                {/* Vizit */}
                <th><span className={styles.colHeaderContent}>Vizit Tarihi <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th>Başlangıç Saati</th>
                {/* Protokol & CAPA */}
                <th>Protokol Sapması?</th>
                <th>CAPA Açıldı mı?</th>
                <th><span className={styles.colHeaderContent}>Yorumlar <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                {/* Tamamlanma Tarihleri */}
                <th><span className={styles.colHeaderContent}>eCRF Tamamlanma <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th><span className={styles.colHeaderContent}>eCRF Tamamlanma Saati <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th><span className={styles.colHeaderContent}>Kaynak Doküman <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th><span className={styles.colHeaderContent}>Kaynak Doküman Saati <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th><span className={styles.colHeaderContent}>İmza Tamamlanma <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th><span className={styles.colHeaderContent}>Süreç Bitiş <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                {/* Süreç & SLA */}
                <th>Cycle Time (gün)</th>
                <th><span className={styles.colHeaderContent}>SLA Hedef (gün) <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                <th>SLA Karşılandı mı?</th>
                <th>SLA Aşıldı mı?</th>
                {/* Sorun */}
                <th>Sorun Oluştu mu?</th>
              </tr>
              {showFilters && (
                <tr className={styles.filterHeaderRow}>
                  {/* Proje Adı - no filter */}
                  <th />
                  {/* Merkez ID */}
                  <th><input className={styles.filterInput} placeholder="Ara..." value={filters.merkez} onChange={e => setFilter('merkez', e.target.value)} /></th>
                  {/* Visit ID */}
                  <th><input className={styles.filterInput} placeholder="Ara..." value={filters.visitNumber} onChange={e => setFilter('visitNumber', e.target.value)} /></th>
                  {/* Visit Tipi */}
                  <th>
                    <select className={styles.filterSelect} value={filters.visitType} onChange={e => setFilter('visitType', e.target.value)}>
                      <option value="">Tümü</option>
                      <option value="hastane">Site</option>
                      <option value="home">Home</option>
                    </select>
                  </th>
                  {/* CRA */}
                  <th><input className={styles.filterInput} placeholder="Ara..." value={filters.siteCoordinator} onChange={e => setFilter('siteCoordinator', e.target.value)} /></th>
                  {/* Hasta ID */}}
                  <th><input className={styles.filterInput} placeholder="Ara..." value={filters.patientId} onChange={e => setFilter('patientId', e.target.value)} /></th>
                  {/* Kaynak Döküman */}
                  <th>
                    <select className={styles.filterSelect} value={filters.sourceDocument} onChange={e => setFilter('sourceDocument', e.target.value)}>
                      <option value="">Tümü</option>
                      <option value="yes">Evet</option>
                      <option value="no">Hayır</option>
                    </select>
                  </th>
                  {/* Kaynak Döküman Saati */}
                  <th><input className={styles.filterInput} placeholder="Ara..." value={filters.sourceDocumentCompletionTime} onChange={e => setFilter('sourceDocumentCompletionTime', e.target.value)} /></th>
                  {/* Screen Fail */}
                  <th>
                    <select className={styles.filterSelect} value={filters.screenFail} onChange={e => setFilter('screenFail', e.target.value)}>
                      <option value="">Tümü</option>
                      <option value="yes">Evet</option>
                      <option value="no">Hayır</option>
                    </select>
                  </th>
                  {/* Drop Patient */}
                  <th>
                    <select className={styles.filterSelect} value={filters.dropPatient} onChange={e => setFilter('dropPatient', e.target.value)}>
                      <option value="">Tümü</option>
                      <option value="yes">Evet</option>
                      <option value="no">Hayır</option>
                    </select>
                  </th>
                  {/* Vizit Tarihi */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.visitDateFrom} onChange={e => setFilter('visitDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.visitDateTo} onChange={e => setFilter('visitDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* Başlangıç Saati - no filter */}
                  <th />
                  {/* Protokol Sapması */}
                  <th>
                    <select className={styles.filterSelect} value={filters.protocolDeviation} onChange={e => setFilter('protocolDeviation', e.target.value)}>
                      <option value="">Tümü</option>
                      <option value="yes">Evet</option>
                      <option value="no">Hayır</option>
                    </select>
                  </th>
                  {/* CAPA */}
                  <th>
                    <select className={styles.filterSelect} value={filters.capaOpened} onChange={e => setFilter('capaOpened', e.target.value)}>
                      <option value="">Tümü</option>
                      <option value="yes">Evet</option>
                      <option value="no">Hayır</option>
                      <option value="n/a">N/A</option>
                    </select>
                  </th>
                  {/* Yorumlar */}
                  <th><input className={styles.filterInput} placeholder="Ara..." value={filters.comments} onChange={e => setFilter('comments', e.target.value)} /></th>
                  {/* eCRF */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.ecrfCompletionDateFrom} onChange={e => setFilter('ecrfCompletionDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.ecrfCompletionDateTo} onChange={e => setFilter('ecrfCompletionDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* Kaynak Doküman */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.sourceDocCompletionDateFrom} onChange={e => setFilter('sourceDocCompletionDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.sourceDocCompletionDateTo} onChange={e => setFilter('sourceDocCompletionDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* İmza */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.signatureCompletionDateFrom} onChange={e => setFilter('signatureCompletionDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.signatureCompletionDateTo} onChange={e => setFilter('signatureCompletionDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* Süreç Bitiş */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.processEndDateFrom} onChange={e => setFilter('processEndDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.processEndDateTo} onChange={e => setFilter('processEndDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* Cycle Time - no filter (calculated) */}
                  <th />
                  {/* SLA Hedef */}
                  <th><input className={styles.filterInput} placeholder="Ara..." value={filters.slaTarget} onChange={e => setFilter('slaTarget', e.target.value)} /></th>
                  {/* SLA Karşılandı, SLA Aşıldı - no filter (calculated) */}
                  <th /><th />
                  {/* Sorun */}
                  <th>
                    <select className={styles.filterSelect} value={filters.issueOccurred} onChange={e => setFilter('issueOccurred', e.target.value)}>
                      <option value="">Tümü</option>
                      <option value="yes">Evet</option>
                      <option value="no">Hayır</option>
                    </select>
                  </th>
                </tr>
              )}
            </thead>
            <tbody>
              {filteredRows.map(({ row, originalIndex }) => {
                const cycleTime = calcCycleTime(row.visitDate, row.processEndDate);
                const slaMet = cycleTime !== '' && row.slaTarget !== ''
                  ? (Number(cycleTime) <= Number(row.slaTarget) ? 'Evet' : 'Hayır') : '';
                const slaOverdue = cycleTime !== '' && row.slaTarget !== ''
                  ? (Number(cycleTime) > Number(row.slaTarget) ? 'Evet' : 'Hayır') : '';
                return (
                  <tr key={originalIndex}>
                    <td className={styles.rowNumber}>
                      {originalIndex + 1}
                      {row._isNew
                        ? <div className={styles.newBadge}>Yeni</div>
                        : <div className={styles.existingBadge}>Mevcut</div>}
                    </td>
                    {/* Proje Adı (read-only) */}
                    <td><span className={styles.cellText}>{getProjectName(row.projectId || selectedProjectId)}</span></td>
                    {/* Merkez */}
                    <td>
                      <select className={styles.cellSelect} value={row.merkez} onChange={e => handleCellChange(originalIndex, 'merkez', e.target.value)}>
                        <option value="">—</option>
                        {hospitals.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                      </select>
                    </td>
                    {/* Visit ID */}
                    <td><input className={styles.cellInput} value={row.visitNumber} onChange={e => handleCellChange(originalIndex, 'visitNumber', e.target.value)} placeholder="—" /></td>
                    {/* Visit Tipi */}
                    <td>
                      <select className={styles.cellSelect} value={row.visitType} onChange={e => handleCellChange(originalIndex, 'visitType', e.target.value)}>
                        <option value="hastane">Site</option>
                        <option value="home">Home</option>
                      </select>
                    </td>
                    {/* CRA */}
                    <td>
                      <select className={styles.cellSelect} value={row.siteCoordinator} onChange={e => handleCellChange(originalIndex, 'siteCoordinator', e.target.value)}>
                        <option value="">—</option>
                        {cras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    {/* Hasta ID */}
                    <td><input className={styles.cellInput} value={row.patientId} onChange={e => handleCellChange(originalIndex, 'patientId', e.target.value)} placeholder="—" /></td>
                    {/* Kaynak Döküman */}
                    <td>
                      <select className={styles.cellSelect} value={row.sourceDocument} onChange={e => handleCellChange(originalIndex, 'sourceDocument', e.target.value)}>
                        <option value="">—</option>
                        <option value="yes">Evet</option>
                        <option value="no">Hayır</option>
                      </select>
                    </td>
                    {/* Kaynak Döküman Saati */}
                    <td><input type="time" className={`${styles.cellInput} ${styles.cellInputTime}`} value={row.sourceDocumentCompletionTime} onChange={e => handleCellChange(originalIndex, 'sourceDocumentCompletionTime', e.target.value)} /></td>
                    {/* Screen Fail */}
                    <td>
                      <select className={styles.cellSelect} value={row.screenFail} onChange={e => handleCellChange(originalIndex, 'screenFail', e.target.value)}>
                        <option value="">—</option>
                        <option value="yes">Evet</option>
                        <option value="no">Hayır</option>
                      </select>
                    </td>
                    {/* Drop Patient */}
                    <td>
                      <select className={styles.cellSelect} value={row.dropPatient} onChange={e => handleCellChange(originalIndex, 'dropPatient', e.target.value)}>
                        <option value="">—</option>
                        <option value="yes">Evet</option>
                        <option value="no">Hayır</option>
                      </select>
                    </td>
                    {/* Vizit Tarihi */}
                    <td><input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`} value={row.visitDate} onChange={e => handleCellChange(originalIndex, 'visitDate', e.target.value)} /></td>
                    {/* Başlangıç Saati */}
                    <td><input type="time" className={`${styles.cellInput} ${styles.cellInputTime}`} value={row.visitStartTime} onChange={e => handleCellChange(originalIndex, 'visitStartTime', e.target.value)} /></td>
                    {/* Protokol Sapması */}
                    <td>
                      <select className={styles.cellSelect} value={row.protocolDeviation} onChange={e => handleCellChange(originalIndex, 'protocolDeviation', e.target.value)}>
                        <option value="">—</option>
                        <option value="yes">Evet</option>
                        <option value="no">Hayır</option>
                      </select>
                    </td>
                    {/* CAPA Açıldı mı */}
                    <td>
                      <select className={styles.cellSelect} value={row.capaOpened} onChange={e => handleCellChange(originalIndex, 'capaOpened', e.target.value)}>
                        <option value="">—</option>
                        <option value="yes">Evet</option>
                        <option value="no">Hayır</option>
                        <option value="n/a">N/A</option>
                      </select>
                    </td>
                    {/* Yorumlar */}
                    <td><input className={styles.cellInput} value={row.comments} onChange={e => handleCellChange(originalIndex, 'comments', e.target.value)} placeholder="—" /></td>
                    {/* eCRF Tamamlanma */}
                    <td><input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`} value={row.ecrfCompletionDate} onChange={e => handleCellChange(originalIndex, 'ecrfCompletionDate', e.target.value)} /></td>
                    {/* eCRF Tamamlanma Saati */}
                    <td><input type="time" className={`${styles.cellInput} ${styles.cellInputTime}`} value={row.ecrfCompletionTime} onChange={e => handleCellChange(originalIndex, 'ecrfCompletionTime', e.target.value)} /></td>
                    {/* Kaynak Doküman */}
                    <td><input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`} value={row.sourceDocCompletionDate} onChange={e => handleCellChange(originalIndex, 'sourceDocCompletionDate', e.target.value)} /></td>
                    {/* Kaynak Doküman Saati */}
                    <td><input type="time" className={`${styles.cellInput} ${styles.cellInputTime}`} value={row.sourceDocCompletionTime} onChange={e => handleCellChange(originalIndex, 'sourceDocCompletionTime', e.target.value)} /></td>
                    {/* İmza Tamamlanma */}
                    <td><input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`} value={row.signatureCompletionDate} onChange={e => handleCellChange(originalIndex, 'signatureCompletionDate', e.target.value)} /></td>
                    {/* Süreç Bitiş Tarihi */}
                    <td><input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`} value={row.processEndDate} onChange={e => handleCellChange(originalIndex, 'processEndDate', e.target.value)} /></td>
                    {/* Cycle Time (calculated, read-only) */}
                    <td><span className={styles.cellText}>{cycleTime || '—'}</span></td>
                    {/* SLA Hedef */}
                    <td><input type="number" min="0" className={`${styles.cellInput} ${styles.cellInputNumber}`} value={row.slaTarget} onChange={e => handleCellChange(originalIndex, 'slaTarget', e.target.value)} placeholder="0" /></td>
                    {/* SLA Karşılandı (calculated, read-only) */}
                    <td>
                      <span className={`${styles.cellText} ${slaMet === 'Evet' ? styles.slaMetYes : slaMet === 'Hayır' ? styles.slaMetNo : ''}`}>
                        {slaMet || '—'}
                      </span>
                    </td>
                    {/* SLA Aşıldı (calculated, read-only) */}
                    <td>
                      <span className={`${styles.cellText} ${slaOverdue === 'Evet' ? styles.slaOverdueYes : ''}`}>
                        {slaOverdue || '—'}
                      </span>
                    </td>
                    {/* Sorun Oluştu mu */}
                    <td>
                      <select className={styles.cellSelect} value={row.issueOccurred} onChange={e => handleCellChange(originalIndex, 'issueOccurred', e.target.value)}>
                        <option value="">—</option>
                        <option value="yes">Evet</option>
                        <option value="no">Hayır</option>
                      </select>
                    </td>
                    {/* Delete */}
                    <td style={{ textAlign: 'center' }}>
                      <button className={styles.deleteRowButton} onClick={() => removeRow(originalIndex)} title="Satırı kaldır">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerInfo}>
          {rows.length > 0 && (
            <>
              <span>{hasActiveFilters ? `${filteredRows.length} / ${rows.length} satır` : `${rows.length} satır`}</span>
              {existingCount > 0 && <span className={styles.existingBadge}>Mevcut: {existingCount}</span>}
              {newCount > 0 && <span className={styles.newBadge}>Yeni: {newCount}</span>}
            </>
          )}
        </div>
        <div className={styles.footerActions}>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={saving || rows.length === 0}
          >
            {saving ? (
              <span className={styles.saving}><span className={styles.spinner} />Kaydediliyor...</span>
            ) : (
              <><FaSave /> Tümünü Kaydet</>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default HastaneDataEntryContent;
